// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import React, { useEffect, useRef } from 'react';
import snakeCase from 'lodash/snakeCase';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from 'util/commons';
import { TraceDataDetail } from 'types/purchase';
import TraceBoardCard from './TraceBoardCard';
import styles from './PurchaseTraceBoard.module.scss';
import { nodeKey } from 'util/purchaseBoardUtils';
import { OperatorType } from 'util/Enums';

type Props = {
  index: number;
  column: TraceDataDetail;
  selectedSiteId: string | null;
  highlightedSiteIds: Set<string>;
  hasSelection: boolean;
  onCardClick: (siteId: string) => void;
  /** Called with the wrapper div element so TraceBoardLines can read getBoundingClientRect. */
  onCardRef: (siteId: string, el: HTMLElement | null) => void;
};

const TraceBoardColumn: React.FC<Props> = ({
  index,
  column,
  selectedSiteId,
  highlightedSiteIds,
  hasSelection,
  onCardClick,
  onCardRef,
}) => {
  const { t } = useTranslation();

  // Track wrapper elements locally so we can scroll to the first highlighted card.
  const wrapperRefs = useRef<Map<string, HTMLElement | null>>(new Map());
  const columnBodyRef = useRef<HTMLDivElement>(null);

  // When the highlighted set changes, scroll the column body (vertical only)
  // so the first highlighted card in this column is visible.
  useEffect(() => {
    if (!highlightedSiteIds.size) return;
    const first = column.sites.find((s) =>
      highlightedSiteIds.has(nodeKey(s.siteId, column.stageIndex))
    );
    if (!first) return;
    const el = wrapperRefs.current.get(
      nodeKey(first.siteId, column.stageIndex)
    );
    const body = columnBodyRef.current;
    if (!el || !body) return;
    // Use getBoundingClientRect for both so the comparison is in the same
    // coordinate space, unaffected by which element is the offsetParent.
    const cardRect = el.getBoundingClientRect();
    const bodyRect = body.getBoundingClientRect();
    const topDelta = cardRect.top - bodyRect.top; // <0 → card is above visible area
    const bottomDelta = cardRect.bottom - bodyRect.bottom; // >0 → card is below visible area
    if (topDelta < 0) {
      body.scrollBy({ top: topDelta, behavior: 'smooth' });
    } else if (bottomDelta > 0) {
      body.scrollBy({ top: bottomDelta, behavior: 'smooth' });
    }
  }, [highlightedSiteIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only enable sticky when exactly 1 card in this column is highlighted.
  // Multiple sticky items with the same top/bottom anchor overlap each other,
  // causing getBoundingClientRect() to return the same position for both
  // cards, which makes two SVG lines appear to originate from the same point.
  const MAX_STICKY = 1;
  const highlightedCountInColumn = column.sites.filter((s) =>
    highlightedSiteIds.has(nodeKey(s.siteId, column.stageIndex))
  ).length;
  const useStickyInColumn = highlightedCountInColumn <= MAX_STICKY;

  const getColumnLabel = () => {
    let stageName = t(
      `purchase_page.trace_data_detail.stages.${snakeCase(column.stage)}`,
      { defaultValue: column.stage }
    );

    if (column.stageIndex % 1 !== 0) {
      if (column.stage === OperatorType.Compressions) {
        stageName = t(
          'purchase_page.trace_data_detail.stages.middle_compression'
        );
      } else if (column.stage === OperatorType.Recycle) {
        stageName = t('purchase_page.trace_data_detail.stages.middle_recycle');
      } else {
        stageName = `${t(
          'purchase_page.trace_data_detail.middle_process'
        )} (${stageName})`;
      }
    }

    return `${index + 1}. ${stageName}`;
  };

  return (
    <div className={styles.column}>
      <div className={styles.columnHeader}>{getColumnLabel()}</div>

      <div ref={columnBodyRef} className={styles.columnBody}>
        {column.sites.map((site) => {
          const nk = nodeKey(site.siteId, column.stageIndex);
          const isHighlighted = highlightedSiteIds.has(nk);
          const isSelected = selectedSiteId === nk;
          const isDimmed = hasSelection && !isHighlighted;

          return (
            <div
              key={site.siteId}
              /**
               * Highlighted cards use `position: sticky` so they remain visible
               * while scrolling the column. CSS sticky pins to both top and bottom
               * of the scroll container, ensuring the card never leaves view.
               * getBoundingClientRect() always returns the actual visual position
               * (even when sticky), so lines always connect to the visible card.
               */
              className={mergeClasses(styles.cardWrapper, {
                [styles.cardWrapperHighlighted]: isHighlighted,
                [styles.cardWrapperSticky]: isHighlighted && useStickyInColumn,
              })}
              ref={(el) => {
                const nk = nodeKey(site.siteId, column.stageIndex);
                wrapperRefs.current.set(nk, el);
                onCardRef(nk, el);
              }}
            >
              <TraceBoardCard
                stageId={column.id}
                stageIndex={column.stageIndex}
                info={site}
                stage={column.stage}
                isSelected={isSelected}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
                onClick={() =>
                  onCardClick(nodeKey(site.siteId, column.stageIndex))
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(TraceBoardColumn);
